import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import { mockMentorStudents } from '@/data/mentor-mock-data'
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    LineChart, Line, PieChart, Pie, Cell
} from 'recharts'
import { TrendingUp, BarChart3, Users, Clock, Award } from 'lucide-react'

export function MentorAnalyticsView() {
    // Mock Data for Charts
    const performanceTrend = [
        { month: 'Jul', batchA: 65, batchB: 60 },
        { month: 'Aug', batchA: 68, batchB: 62 },
        { month: 'Sep', batchA: 72, batchB: 65 },
        { month: 'Oct', batchA: 75, batchB: 68 },
    ]

    const subjectPerformance = [
        { subject: 'Physics', avg: 72, target: 80 },
        { subject: 'Chemistry', avg: 68, target: 80 },
        { subject: 'Biology', avg: 78, target: 85 },
    ]

    const distributionData = [
        { name: '> 650', value: 15 },
        { name: '550-650', value: 35 },
        { name: '450-550', value: 30 },
        { name: '< 450', value: 20 },
    ]
    const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444']

    const topPerformers = [...mockMentorStudents]
        .sort((a, b) => b.avgTestScore - a.avgTestScore)
        .slice(0, 5)

    return (
        <div className="space-y-6 pb-24 lg:pb-0">
            {/* Header */}
            <div>
                <h2 className="text-2xl font-bold text-theme-primary">Student Analytics</h2>
                <p className="text-sm text-theme-secondary">Detailed performance insights and trends</p>
            </div>

            {/* Quick Stats Row */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <GlassmorphicCard className="p-4">
                    <div className="flex justify-between items-start">
                        <div>
                            <p className="text-sm text-theme-secondary">Avg Batch Score</p>
                            <h3 className="text-2xl font-bold text-theme-primary mt-1">585</h3>
                            <span className="text-xs text-green-500 flex items-center mt-1">
                                <TrendingUp className="h-3 w-3 mr-1" /> +12 points
                            </span>
                        </div>
                        <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                            <BarChart3 className="h-5 w-5 text-blue-600" />
                        </div>
                    </div>
                </GlassmorphicCard>
                <GlassmorphicCard className="p-4">
                    <div className="flex justify-between items-start">
                        <div>
                            <p className="text-sm text-theme-secondary">Total Students</p>
                            <h3 className="text-2xl font-bold text-theme-primary mt-1">{mockMentorStudents.length}</h3>
                            <span className="text-xs text-theme-secondary flex items-center mt-1">
                                Across 2 batches
                            </span>
                        </div>
                        <div className="p-2 bg-indigo-100 dark:bg-indigo-900/20 rounded-lg">
                            <Users className="h-5 w-5 text-indigo-600" />
                        </div>
                    </div>
                </GlassmorphicCard>
                <GlassmorphicCard className="p-4">
                    <div className="flex justify-between items-start">
                        <div>
                            <p className="text-sm text-theme-secondary">Avg Study Hours</p>
                            <h3 className="text-2xl font-bold text-theme-primary mt-1">6.8h</h3>
                            <span className="text-xs text-green-500 flex items-center mt-1">
                                <TrendingUp className="h-3 w-3 mr-1" /> +0.5h vs last week
                            </span>
                        </div>
                        <div className="p-2 bg-amber-100 dark:bg-amber-900/20 rounded-lg">
                            <Clock className="h-5 w-5 text-amber-600" />
                        </div>
                    </div>
                </GlassmorphicCard>
                <GlassmorphicCard className="p-4">
                    <div className="flex justify-between items-start">
                        <div>
                            <p className="text-sm text-theme-secondary">Top Score</p>
                            <h3 className="text-2xl font-bold text-theme-primary mt-1">700</h3>
                            <span className="text-xs text-theme-secondary flex items-center mt-1">
                                Divya Nair
                            </span>
                        </div>
                        <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                            <Award className="h-5 w-5 text-purple-600" />
                        </div>
                    </div>
                </GlassmorphicCard>
            </div>

            {/* Charts Row 1 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <GlassmorphicCard className="p-6">
                    <h3 className="font-bold text-theme-primary mb-4">Batch Performance Trend</h3>
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={performanceTrend}>
                                <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                                <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} />
                                <YAxis stroke="#94a3b8" fontSize={12} />
                                <Tooltip
                                    contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.8)', borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                />
                                <Legend />
                                <Line type="monotone" dataKey="batchA" stroke="#3B82F6" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} name="Batch A" />
                                <Line type="monotone" dataKey="batchB" stroke="#10B981" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} name="Batch B" />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </GlassmorphicCard>

                <GlassmorphicCard className="p-6">
                    <h3 className="font-bold text-theme-primary mb-4">Subject-wise Average</h3>
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={subjectPerformance}>
                                <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                                <XAxis dataKey="subject" stroke="#94a3b8" fontSize={12} />
                                <YAxis stroke="#94a3b8" fontSize={12} />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                        borderRadius: '12px',
                                        border: 'none',
                                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                                    }}
                                    itemStyle={{ color: '#1e293b' }}
                                />
                                <Legend />
                                <Bar dataKey="avg" fill="#3B82F6" radius={[4, 4, 0, 0]} name="Batch Average" />
                                <Bar dataKey="target" fill="#94a3b8" radius={[4, 4, 0, 0]} name="Target" opacity={0.3} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </GlassmorphicCard>
            </div>

            {/* Charts Row 2 & Top Performers */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <GlassmorphicCard className="p-6 lg:col-span-1">
                    <h3 className="font-bold text-theme-primary mb-4">Score Distribution</h3>
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={distributionData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={80}
                                    paddingAngle={5}
                                    dataKey="value"
                                >
                                    {distributionData.map((_, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </GlassmorphicCard>

                <GlassmorphicCard className="p-6 lg:col-span-2">
                    <h3 className="font-bold text-theme-primary mb-4">Top Performers</h3>
                    <div className="space-y-4">
                        {topPerformers.map((student, index) => (
                            <div key={student.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                                <div className="flex items-center gap-3">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-slate-400' : index === 2 ? 'bg-amber-600' : 'bg-slate-300 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                                        }`}>
                                        {index + 1}
                                    </div>
                                    <div>
                                        <p className="font-semibold text-theme-primary">{student.name}</p>
                                        <p className="text-xs text-theme-secondary">{student.batch}</p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="font-bold text-primary-blue">{student.avgTestScore} avg</p>
                                    <p className="text-xs text-green-500 capitalize">{student.performanceTrend.replace('-', ' ')}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </GlassmorphicCard>
            </div>
        </div>
    )
}
